import {Box} from 'src/design/atoms/Box';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import type {FC} from 'react';
import {Button} from 'src/design/atoms/Button';
import {
  IconSize,
  MailIcon,
  MailOffIcon,
} from 'src/design/atoms/icons/CommonIcons';
import {Pill} from 'src/design/atoms/Pill';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {SupportDetails} from './SupportDetails';

export interface NotificationEmailsCardProps {
  enabled: boolean;
  onTurnOn: () => void;
  onTurnOff: () => void;
  turningOn: boolean;
  turningOff: boolean;
  disabled: boolean;
}

export const NotificationEmailsCard: FC<NotificationEmailsCardProps> = ({
  enabled,
  onTurnOn,
  onTurnOff,
  turningOn,
  turningOff,
  disabled,
}) => (
  <SectionCard
    title="Notification Emails"
    icon={<MailIcon size={IconSize.md} />}
    rightSection={
      enabled ? (
        <Pill text="On" color="green" />
      ) : (
        <Pill text="Off" color="gray" />
      )
    }>
    <Stack p="md" gap="md">
      {enabled ? (
        <Text size="sm">Notification emails are enabled for this company.</Text>
      ) : (
        <Box>
          <Text size="sm">
            Notification emails are turned off for this company.
          </Text>
          <Text size="sm" c="dimmed">
            The user unsubscribed, or an administrator turned them off. The
            audit trail shows who and when.
          </Text>
        </Box>
      )}
      <Text size="sm" c="dimmed">
        Delivery also depends on notification rules and email suppression. This
        setting does not control sign-in or password reset emails.
      </Text>
      <Box>
        {enabled ? (
          <Button
            variant="outline"
            color="red"
            onClick={onTurnOff}
            loading={turningOff}
            disabled={disabled}
            leftSection={<MailOffIcon />}>
            Turn off
          </Button>
        ) : (
          <Button
            color="green"
            onClick={onTurnOn}
            loading={turningOn}
            disabled={disabled}
            leftSection={<MailIcon />}>
            Turn on
          </Button>
        )}
      </Box>
      <SupportDetails title="Help with notification preferences">
        <Text size="sm">
          A user can unsubscribe from a notification email. Only an
          administrator can turn notifications back on. Confirm that the user
          wants them before turning them on again.
        </Text>
        <Text size="sm">
          The Personnel Audit Log records preference changes and when they
          happened. Ask an administrator with audit access if that tab is
          unavailable. Turning notifications on does not remove email
          suppression or resend missed notifications.
        </Text>
      </SupportDetails>
    </Stack>
  </SectionCard>
);
